import { Component, computed, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core'
import { IlsModelService } from '../../shared/services/ils-model.service'
import { TokenService, UserPlayer } from '@monorepo/auth'
import { Router } from '@angular/router'
import { SendLogActionService } from '../../shared/services/send-log-action.service'
import { CachingStorageService } from '../../shared/services/caching-storage.service'
import { environment } from '../../../environments/environment'
import { MatDialog } from '@angular/material/dialog'
import { take } from 'rxjs'
import { NgClass } from '@angular/common'
import { MatToolbar } from '@angular/material/toolbar'
import { TranslatePipe } from '@ngx-translate/core'
import { MatTooltip } from '@angular/material/tooltip'
import { MatIcon } from '@angular/material/icon'
import { ContactGolabComponent, DuplicateIlsDialogComponent, MessageDialogComponent } from '@monorepo/angular-common'
import { StudentGroupContentService } from '../../shared/services/student-group-content.service'

interface ToolbarGroupMember {
  _id: string
  nickname: string
  isCurrentUser: boolean
}

@Component({
  selector: 'app-title-bar',
  templateUrl: './title-bar.component.html',
  styleUrls: ['./title-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass, MatToolbar, TranslatePipe, MatIcon, MatTooltip, ContactGolabComponent],
})
export class TitleBarComponent {
  private readonly ilsModelService = inject(IlsModelService)
  private readonly sendLogActionService = inject(SendLogActionService)
  private readonly cachingStorageService = inject(CachingStorageService)
  private readonly tokenService = inject(TokenService)
  protected readonly dialog = inject(MatDialog)
  private readonly router = inject(Router)
  private readonly studentGroupContentService = inject(StudentGroupContentService)

  nickname = ''
  @Input() burgerOpened = false
  @Output() burgerClicked = new EventEmitter()

  readonly hasCollaborationGroup = computed(
    () => this.studentGroupContentService.studentGroupContent()?.isInGroup === true,
  )

  readonly groupMembers = computed<ToolbarGroupMember[]>(() => {
    const groupContent = this.studentGroupContentService.studentGroupContent()
    if (!groupContent?.isInGroup) {
      return []
    }
    const members = groupContent.members.map((member) => ({
      _id: member._id,
      nickname: member.nickname,
      isCurrentUser: member._id === groupContent.studentId,
    }))

    if (!members.some((member) => member.isCurrentUser) && this.nickname) {
      return [
        {
          _id: groupContent.studentId,
          nickname: this.nickname,
          isCurrentUser: true,
        },
        ...members,
      ]
    }

    return members
  })

  constructor() {
    this.nickname = (this.tokenService.getUser() as UserPlayer)?.nickname ?? ''
  }

  get title(): string {
    return this.ilsModelService.title$.value
  }

  get previewMode(): boolean {
    return this.ilsModelService.published
  }

  get published(): boolean {
    return this.ilsModelService.published
  }

  logout() {
    if (this.ilsModelService.ilsLoaded$) {
      this.sendLogActionService.sendLeavePhaseAction()
    }
    this.sendLogActionService.sendLeaveIlsAction()
    this.sendLogActionService.sendLogoutAction()
    if (this.ilsModelService.published) {
      this.sendLogActionService.clear()
      this.cachingStorageService.clear()
    }
    this.studentGroupContentService.clearStudentGroupContent()
    this.tokenService.signOut()
    this.router.navigate(['login', this.ilsModelService.ilsId])
  }

  togglePhasesClicked() {
    this.burgerClicked.emit()
  }

  openUTInfoDialog() {
    this.dialog.open(MessageDialogComponent, {
      data: {
        title: 'aboutUTDialog.title',
        htmlMessage: 'aboutUTDialog.htmlMessage',
        logos: [
          {
            src: 'assets/shared-images/ut-logos/twoLineUTLogo.svg',
            url: 'https://www.utwente.nl/en/',
          },
          {
            src: 'assets/shared-images/golab-logos/golab-logo.svg',
            url: 'https://maken.wikiwijs.nl/212411/Go_Lab_page___English',
          },
          {
            src: 'assets/shared-images/iol/coloredLogoWithWhiteBackground.svg',
            url: 'https://www.openleermateriaal.nl/',
          },
        ],
      },
    })
  }

  duplicateIls() {
    console.log('duplicateIls')
    if (!this.published) {
      return
    }
    const askDialogRef = this.dialog.open(DuplicateIlsDialogComponent)
    askDialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((duplicate) => {
        if (duplicate) {
          const ilsDuplicateUrl = environment.editorURL + '/editor/duplicate/' + this.ilsModelService.ilsId
          window.open(ilsDuplicateUrl, '_ilsEditor')
        }
      })
  }
}
